import { useCallback, useSyncExternalStore } from 'react';
import type { ServerMessage } from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import { sendResultUpload } from 'ts-components/PiChat/presignedPut';
import { useFrameHandler } from '../useFrameHandler';
import {
  createKiCodeApproval,
  type ApprovalCardRecord,
} from './approvalController';
import {
  hasPendingApproval,
  type ApprovalButtonId,
} from './approvalPresentation';

export interface KiCodeApproval {
  handleServerMessage: (message: ServerMessage, meta: PiChatFrameMeta) => void;
  /** This chat's cards, pending and settled, in arrival order. */
  cards: readonly ApprovalCardRecord[];
  /** A card waits on the user: sending is blocked until it settles. */
  pending: boolean;
  press(id: ApprovalButtonId): void;
  /** The user stopped the turn: a pending card answers declined. */
  cancel(): void;
  /** Synchronous lock for the socket send, read from the controller rather than render state. */
  isSendBlocked(): boolean;
}

const NO_CARDS: readonly ApprovalCardRecord[] = [];
const subscribeNone = () => () => {};
const getNoCards = () => NO_CARDS;

export const useKiCodeApproval = (enabled: boolean): KiCodeApproval => {
  const { instance: wiring, handle: handleServerMessage } = useFrameHandler(
    enabled,
    () => createKiCodeApproval({ sendResult: sendResultUpload })
  );

  const controller = wiring?.controller;

  const cards = useSyncExternalStore(
    controller?.subscribe ?? subscribeNone,
    controller?.getCards ?? getNoCards
  );

  const press = useCallback(
    (id: ApprovalButtonId) => {
      if (id === 'approve-once') controller?.approveOnce();
      else if (id === 'approve-window') controller?.approveWindow();
      else controller?.decline();
    },
    [controller]
  );

  const cancel = useCallback(() => controller?.cancel(), [controller]);
  const isSendBlocked = useCallback(
    () => wiring?.isSendBlocked() ?? false,
    [wiring]
  );

  return {
    handleServerMessage,
    cards,
    pending: hasPendingApproval(cards),
    press,
    cancel,
    isSendBlocked,
  };
};
